// <pos-stations-settings/> — 此裝置站點角色 + 訂單分流通知規則
// 路徑：#/settings/stations（owner / manager）
// - 上半：此裝置是「櫃檯 / 後廚 / 外場 / 全通知」（存 localStorage，每台自己設）
// - 下半：整店通知規則（哪個 status 進來要通知哪些 role）存 DB
// see src/lib/notification-routes.js

import {
  STATION_ROLES,
  STATION_LABELS,
  STATUS_LABELS_ZH,
  DEFAULT_ROUTES,
  getStationRole,
  setStationRole,
  getStationName,
  setStationName,
} from '../lib/notification-routes.js';
import { listNotificationRoutes, upsertNotificationRoute } from '../lib/api.js';

const MANAGED_STATUSES = ['pending','paid','preparing','ready','completed','cancelled'];

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
}

class PosStationsSettings extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.state = {
      role: getStationRole(),
      name: getStationName(),
      routes: [],       // DB rows
      loading: true,
      error: null,
      savingStatus: null,
    };
  }

  async connectedCallback() {
    this.render();
    await this.loadRoutes();
  }

  async loadRoutes() {
    try {
      const { routes } = await listNotificationRoutes();
      this.state.routes = routes || [];
    } catch (err) {
      this.state.error = err.message_zh || err.message || '載入通知規則失敗';
    } finally {
      this.state.loading = false;
      this.render();
    }
  }

  _routeFor(status) {
    const row = this.state.routes.find(r => r.to_status === status);
    if (row) {
      return {
        notify_roles: row.notify_roles || [],
        sound: row.sound !== false,
        flash: row.flash !== false,
        _source: 'db',
      };
    }
    const d = DEFAULT_ROUTES[status] || { notify_roles: [], sound: false, flash: false };
    return { ...d, _source: 'default' };
  }

  async toggleRouteRole(status, role) {
    const cur = this._routeFor(status);
    const roles = new Set(cur.notify_roles);
    if (roles.has(role)) roles.delete(role);
    else roles.add(role);
    await this.saveRoute(status, { ...cur, notify_roles: [...roles] });
  }

  async toggleRouteFlag(status, flag) {
    const cur = this._routeFor(status);
    await this.saveRoute(status, { ...cur, [flag]: !cur[flag] });
  }

  async saveRoute(status, next) {
    this.state.savingStatus = status;
    this.render();
    try {
      const { route } = await upsertNotificationRoute({
        to_status: status,
        notify_roles: next.notify_roles,
        sound: next.sound,
        flash: next.flash,
      });
      const idx = this.state.routes.findIndex(r => r.to_status === status);
      if (idx >= 0) this.state.routes[idx] = route;
      else this.state.routes.push(route);
    } catch (err) {
      alert('儲存失敗：' + (err.message_zh || err.message || '未知錯誤'));
    } finally {
      this.state.savingStatus = null;
      this.render();
    }
  }

  render() {
    const { role, name, loading, error, savingStatus } = this.state;

    this.shadowRoot.innerHTML = `
      <style>${this.styles()}</style>
      <main>
        <aside class="sub-nav">
          <div class="sub-nav__title">設定</div>
          <a class="sub-nav__item" href="#/settings">店家資料</a>
          <a class="sub-nav__item" href="#/settings/qr">桌位 QR Code</a>
          <a class="sub-nav__item" href="#/settings/reservation">訂位設定</a>
          <a class="sub-nav__item is-active" href="#/settings/stations">站點與通知</a>
          <a class="sub-nav__item" href="#/staff">員工</a>
          <a class="sub-nav__item" href="#/settings/ai-keys">AI Keys</a>
        </aside>

        <section class="panel">
          <div class="panel__head">
            <h1 class="panel__title">站點與分流通知</h1>
            <p class="panel__desc">
              每台裝置各自指定角色（櫃檯／後廚／外場），訂單進到不同狀態時只有該角色裝置會閃+響。
              無紙化環保流程的關鍵。
            </p>
          </div>

          <div class="card">
            <h2 class="card__title">此裝置</h2>
            <p class="card__desc">只存在這台裝置的瀏覽器裡，重新登入不會影響。</p>

            <div class="field">
              <label>角色</label>
              <div class="roles">
                ${STATION_ROLES.map(r => `
                  <button class="role-btn" data-act="set-role" data-role="${r}" data-selected="${role === r}">
                    ${STATION_LABELS[r]}
                  </button>
                `).join('')}
              </div>
              <div class="field__hint">
                ${role === 'all'
                  ? '目前「全通知」：所有分流事件這台都會響（單機店家適用）'
                  : `目前「${STATION_LABELS[role]}」：只有指定給此角色的狀態會通知`}
              </div>
            </div>

            <div class="field">
              <label>裝置別名（選填）</label>
              <input id="stationName" type="text" maxlength="40" value="${escapeHtml(name)}" placeholder="例：吧台 iPad、廚房 Android TV" />
              <div class="field__hint">只用於自己辨識，方便之後擴充設定時區分裝置</div>
            </div>
          </div>

          <div class="card">
            <h2 class="card__title">訂單狀態 → 通知對象</h2>
            <p class="card__desc">
              例：「付款完成待準備」勾選「後廚」，客人付完款後只有廚房裝置會收到叮咚。
              空白的項目會套用系統預設（見 hint 欄）。
            </p>
            ${error ? `<div class="banner err">✗ ${escapeHtml(error)}</div>` : ''}
            ${loading ? `<div class="banner loading">載入中…</div>` : `
              <table class="routes">
                <thead>
                  <tr>
                    <th>進入狀態</th>
                    ${STATION_ROLES.filter(r => r !== 'all').map(r => `<th>${STATION_LABELS[r]}</th>`).join('')}
                    <th>閃爍</th>
                    <th>聲音</th>
                    <th>來源</th>
                  </tr>
                </thead>
                <tbody>
                  ${MANAGED_STATUSES.map(s => {
                    const rt = this._routeFor(s);
                    const saving = savingStatus === s;
                    return `
                      <tr data-status="${s}" ${saving ? 'aria-busy="true"' : ''}>
                        <td><strong>${escapeHtml(STATUS_LABELS_ZH[s] || s)}</strong></td>
                        ${STATION_ROLES.filter(r => r !== 'all').map(r => `
                          <td class="cell">
                            <label class="chk">
                              <input type="checkbox" data-act="toggle-role" data-status="${s}" data-role="${r}"
                                     ${rt.notify_roles.includes(r) ? 'checked' : ''}
                                     ${saving ? 'disabled' : ''} />
                            </label>
                          </td>
                        `).join('')}
                        <td class="cell">
                          <label class="chk">
                            <input type="checkbox" data-act="toggle-flag" data-status="${s}" data-flag="flash"
                                   ${rt.flash ? 'checked' : ''} ${saving ? 'disabled' : ''} />
                          </label>
                        </td>
                        <td class="cell">
                          <label class="chk">
                            <input type="checkbox" data-act="toggle-flag" data-status="${s}" data-flag="sound"
                                   ${rt.sound ? 'checked' : ''} ${saving ? 'disabled' : ''} />
                          </label>
                        </td>
                        <td class="cell source">${rt._source === 'db' ? '已客製' : '預設'}</td>
                      </tr>
                    `;
                  }).join('')}
                </tbody>
              </table>
            `}
          </div>
        </section>
      </main>
    `;

    this.wireEvents();
  }

  wireEvents() {
    const root = this.shadowRoot;
    root.querySelectorAll('[data-act="set-role"]').forEach(btn => {
      btn.addEventListener('click', () => {
        const r = btn.dataset.role;
        setStationRole(r);
        this.state.role = r;
        this.render();
      });
    });
    const nameInput = root.querySelector('#stationName');
    if (nameInput) nameInput.addEventListener('change', (e) => {
      setStationName(e.target.value);
      this.state.name = e.target.value;
    });
    root.querySelectorAll('[data-act="toggle-role"]').forEach(chk => {
      chk.addEventListener('change', () => {
        this.toggleRouteRole(chk.dataset.status, chk.dataset.role);
      });
    });
    root.querySelectorAll('[data-act="toggle-flag"]').forEach(chk => {
      chk.addEventListener('change', () => {
        this.toggleRouteFlag(chk.dataset.status, chk.dataset.flag);
      });
    });
  }

  styles() {
    return `
      :host { display: block; color: var(--fg-base); background: var(--bg-base); min-height: calc(100vh - 60px); font-family: var(--font-sans); }
      main { display: grid; grid-template-columns: 220px minmax(0, 1fr); gap: var(--s-6); padding: var(--s-6) var(--s-8); }

      .sub-nav { display: grid; gap: var(--s-1); align-content: start; }
      .sub-nav__title { font-size: .75rem; text-transform: uppercase; letter-spacing: 2px; color: var(--fg-dim); padding: 0 var(--s-3) var(--s-2); }
      .sub-nav__item { padding: var(--s-3) var(--s-4); color: var(--fg-muted); text-decoration: none; border-radius: var(--r-md); font-size: .9rem; }
      .sub-nav__item:hover { background: var(--bg-elevated); color: var(--fg-base); }
      .sub-nav__item.is-active { background: var(--bg-elevated); color: var(--brand-primary); border-left: 3px solid var(--brand-primary); padding-left: calc(var(--s-4) - 3px); }

      .panel { display: grid; gap: var(--s-5); align-content: start; }
      .panel__head { display: grid; gap: var(--s-2); }
      .panel__title { font-size: 1.4rem; font-weight: 600; margin: 0; }
      .panel__desc { color: var(--fg-muted); font-size: .9rem; margin: 0; }

      .card { padding: var(--s-5); background: var(--bg-elevated); border: 1px solid var(--border); border-radius: var(--r-lg); display: grid; gap: var(--s-4); }
      .card__title { font-size: 1.05rem; font-weight: 600; margin: 0; }
      .card__desc { color: var(--fg-muted); font-size: .85rem; margin: 0; }

      .field { display: grid; gap: var(--s-2); }
      .field label { font-size: .85rem; color: var(--fg-muted); }
      .field__hint { font-size: .75rem; color: var(--fg-dim); }
      .field input[type=text] { background: var(--bg-base); color: var(--fg-base); border: 1px solid var(--border); border-radius: var(--r-md); padding: var(--s-3); font-family: inherit; font-size: .95rem; }
      .field input[type=text]:focus { outline: none; border-color: var(--brand-primary); }

      .roles { display: flex; flex-wrap: wrap; gap: var(--s-2); }
      .role-btn { padding: var(--s-2) var(--s-4); min-height: 44px; background: var(--bg-surface); color: var(--fg-base); border: 2px solid var(--border); border-radius: var(--r-md); cursor: pointer; font-size: .95rem; font-family: inherit; }
      .role-btn:hover { border-color: var(--border-strong); }
      .role-btn[data-selected="true"] { border-color: var(--brand-primary); background: rgba(232,88,12,.1); color: var(--brand-primary); font-weight: 600; }

      .banner { padding: var(--s-3) var(--s-4); border-radius: var(--r-md); }
      .banner.err { background: rgba(239,68,68,.1); color: var(--danger); }
      .banner.loading { background: rgba(59,130,246,.1); color: var(--fg-muted); }

      table.routes { width: 100%; border-collapse: collapse; font-size: .9rem; }
      table.routes th, table.routes td { padding: var(--s-2) var(--s-3); text-align: left; border-bottom: 1px solid var(--border); }
      table.routes th { font-size: .75rem; text-transform: uppercase; letter-spacing: 1px; color: var(--fg-dim); font-weight: 500; }
      table.routes td.cell { text-align: center; }
      table.routes td.source { color: var(--fg-dim); font-size: .75rem; }
      table.routes tr[aria-busy="true"] { opacity: .5; }
      .chk input { width: 20px; height: 20px; accent-color: var(--brand-primary); cursor: pointer; }

      /* 手機：側欄收成內容上方橫向捲動列，單欄堆疊（放最後蓋過桌面基礎規則） */
      @media (max-width: 768px) {
        main { grid-template-columns: minmax(0, 1fr); gap: var(--s-4); padding: var(--s-4); }
        .sub-nav { grid-auto-flow: column; grid-auto-columns: max-content; overflow-x: auto; }
        .sub-nav__item.is-active { border-left: 0; border-bottom: 3px solid var(--brand-primary); padding-left: var(--s-4); }
        /* 對照表欄位多，改在卡片內橫向捲動，不擠壓字直排 */
        table.routes { display: block; overflow-x: auto; }
        table.routes thead, table.routes tbody, table.routes tr { min-width: 560px; display: table; width: 100%; table-layout: auto; }
      }
    `;
  }
}

customElements.define('pos-stations-settings', PosStationsSettings);
